// CombinationBouquetTask.js
// Kombinacje z powtórzeniami — bukiet k kwiatów z n gatunków

import React from "react";
import { InlineMath, BlockMath } from "react-katex";
import FractionInput from "../../../../components/inputs/FractionInput";

// pomocniczo, stabilne nCk dla małych zakresów
function nCk(n, k) {
  if (k < 0 || n < 0) return 0;
  k = Math.min(k, n - k);
  let num = 1, den = 1;
  for (let i = 1; i <= k; i++) {
    num *= (n - k + i);
    den *= i;
  }
  return Math.floor(num / den);
}

export function generateTask() {
  const n = Math.floor(Math.random() * 4) + 5; // 5..8 gatunków
  const k = Math.floor(Math.random() * 4) + 4; // 4..7 kwiatów w bukiecie

  // Kombinacje z powtórzeniami: C(n+k-1, k)
  const answer = nCk(n + k - 1, k);

  const pool = ["róża", "tulipan", "goździk", "firletka", "frezja", "irys", "lilia", "stokrotka", "chaber", "margerytka"];
  const names = pool.slice(0, n);

  return {
    type: "CombinationBouquetTask",
    data: { n, k, names, answer },
  };
}

export function renderTask({ data }) {
  const { n, k, names } = data;
  const list = names.join(", ");
  return (
    <div style={{ textAlign: "left" }}>
      <p>
        W kwiaciarni dostępnych jest <InlineMath math={`${n}`} /> gatunków kwiatów:
        {" "}<strong>{list}</strong>.
      </p>
      <p>
        Chcemy ułożyć bukiet z <InlineMath math={`${k}`} /> kwiatów.
      </p>
      <p>Na ile sposobów można skomponować taki bukiet?</p>
    </div>
  );
}

export function renderInput(_task, userAnswer, onChange, disabled) {
  return (
    <FractionInput
      value={userAnswer}
      onChange={onChange}
      disabled={disabled}
      label=""
      placeholder="Wpisz liczbę sposobów"
    />
  );
}

export function checkAnswer({ data }, userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object") {
    return { correct: false, message: "Podaj liczbę." };
  }
  const given = parseInt(userAnswer.raw, 10);
  if (Number.isNaN(given)) {
    return { correct: false, message: "Podaj liczbę całkowitą." };
  }
  const correct = given === data.answer;
  userAnswer.correctAnswer = data.answer;
  return { correct, message: correct ? "Dobrze!" : "Spróbuj ponownie." };
}

export function renderAnswer({ data }) {
  return <InlineMath math={`${data.answer}`} />;
}

export function renderUserAnswer(userAnswer) {
  if (!userAnswer || typeof userAnswer !== "object" || !userAnswer.raw) {
    return <i>(brak odpowiedzi)</i>;
  }
  return <InlineMath math={`${userAnswer.raw}`} />;
}

export function renderExplanation({ data }) {
  const { n, k, answer } = data;

  // Parametry do skracania: N = n+k-1, w mianowniku mamy k! i (n-1)!
  const N = n + k - 1;
  const A = k;
  const B = n - 1;

  // skracamy "większą" silnią L! ∈ {k!, (n-1)!}, zostaje krótszy iloczyn i mniej rachunków
  const L = Math.max(A, B);      // większa silnia do skrócenia
  const S = Math.min(A, B);      // mniejsza silnia — zostanie w mianowniku jako S!

  // po skróceniu: N! / L! = N · (N-1) · ... · (L+1)  — dokładnie S czynników
  const topFactorsArr = Array.from({ length: N - L }, (_, i) => N - i); // N, N-1, ..., L+1
  const topLatex = topFactorsArr.join("\\cdot");

  // rozwinięcie S! = S · (S-1) · ... · 1
  const botFactorsArr = Array.from({ length: S }, (_, i) => S - i);
  const botLatex = botFactorsArr.join("\\cdot");

  const topVal = topFactorsArr.reduce((a, b) => a * b, 1);
  const botVal = botFactorsArr.reduce((a, b) => a * b, 1);

  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <p>
        <strong>Krok 1. Dedukcja i wzór.</strong> W bukiecie liczy się zestaw gatunków,
        a ten sam gatunek można dobrać wielokrotnie → to są
        <b> kombinacje z powtórzeniami</b>:
      </p>
      <BlockMath math={`\\text{liczba bukietów}=\\binom{n+k-1}{k}=\\dfrac{(n+k-1)!}{k!(n-1)!}`} />

      <p><strong>Krok 2. Podstawienie, skracanie większą silnią i obliczenie.</strong></p>
      {/* podstawienie liczb */}
      <BlockMath math={`\\binom{${n}+${k}-1}{${k}}=\\dfrac{(${n}+${k}-1)!}{${k}!\\,(${n}-1)!} \\,=\\, \\dfrac{${N}!}{${A}!\\,${B}!}`} />
      {/* skracamy od razu z większą silnią L! i przechodzimy do krótkiego iloczynu / S! */}
      <BlockMath math={`=\\dfrac{${topLatex}}{${S}!}`} />
      {/* rozwijamy S! i liczymy */}
      <BlockMath math={`=\\dfrac{${topLatex}}{${botLatex}}`} />
      <BlockMath math={`=\\dfrac{${topVal}}{${botVal}}=${answer}`} />
    </div>
  );
}
